import { useMemo, useState } from 'react';
import { Check, Film, Image as ImageIcon, Images, Plus, X } from 'lucide-react';
import { canvasImagePreviewUrl } from './canvas-utils.js';

function taskStatus(task) {
  return String(task?.status || task?.state || '').toLowerCase();
}

function taskDate(task) {
  const value = task?.completedAt || task?.updatedAt || task?.createdAt || 0;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function imageAssets(tasks) {
  return (tasks || []).filter((task) => taskStatus(task) === 'completed').flatMap((task) => {
    const images = task.imageUrls || task.images || task.result?.image_urls || [];
    return images.map((item, index) => {
      const url = typeof item === 'string' ? item : item?.url;
      if (!url) return null;
      return {
        id: `${task.id}:${index}`,
        kind: 'image',
        taskId: task.id,
        imageIndex: index,
        url,
        previewUrl: canvasImagePreviewUrl(url, item?.previewUrl),
        downloadUrl: typeof item === 'object' ? item?.downloadUrl || url : url,
        prompt: task.prompt || task.request?.prompt_original || task.request?.prompt || '',
        ratio: task.ratio || task.request?.ratio || '',
        width: Number(item?.width || task.width || task.request?.width || 0),
        height: Number(item?.height || task.height || task.request?.height || 0),
        createdAt: taskDate(task),
        fallbackName: `历史图片${index + 1}`,
      };
    }).filter(Boolean);
  }).sort((left, right) => right.createdAt - left.createdAt);
}

function videoAssets(tasks) {
  return (tasks || []).filter((task) => taskStatus(task) === 'completed' && task.videoUrl).map((task, index) => ({
    id: task.id,
    kind: 'video',
    taskId: task.id,
    url: task.videoUrl,
    previewUrl: task.coverUrl || '',
    poster: task.coverUrl || '',
    downloadUrl: task.downloadUrl || task.videoUrl,
    prompt: task.promptOriginal || task.prompt || '',
    ratio: task.ratio || '16:9',
    duration: Number(task.duration || 0),
    createdAt: taskDate(task),
    fallbackName: `历史视频${index + 1}`,
  })).sort((left, right) => right.createdAt - left.createdAt);
}

function suggestedName(asset) {
  const prompt = String(asset?.prompt || '').replace(/\s+/g, ' ').trim();
  return prompt.slice(0, 40) || asset?.fallbackName || '历史素材';
}

function assetRatio(asset, fallback = '16 / 9') {
  const explicit = String(asset?.ratio || asset?.aspectRatio || '').trim();
  if (/^\d+(?:\.\d+)?:\d+(?:\.\d+)?$/.test(explicit)) return explicit.replace(':', ' / ');
  const width = Number(asset?.width || 0);
  const height = Number(asset?.height || 0);
  return width > 0 && height > 0 ? `${width} / ${height}` : fallback;
}

export default function CanvasAssetLibrary({ tasks = [], videoTasks = [], onAdd, onClose }) {
  const [mode, setMode] = useState('image');
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState('');
  const imagesList = useMemo(() => imageAssets(tasks), [tasks]);
  const videosList = useMemo(() => videoAssets(videoTasks), [videoTasks]);
  const assets = mode === 'image' ? imagesList : videosList;

  const choose = (asset) => {
    setSelected(asset);
    setName(suggestedName(asset));
  };

  const beginDrag = (event, asset) => {
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData('application/x-nebula-library', JSON.stringify({
      ...asset,
      name: suggestedName(asset),
    }));
  };

  return <div className="canvas-library-backdrop" role="presentation" onPointerDown={(event) => {
    if (event.target === event.currentTarget) onClose?.();
  }}>
    <section className="canvas-library" role="dialog" aria-modal="true" aria-label="历史素材库">
      <header>
        <div><strong>素材库</strong><small>选择历史生成内容加入当前画布</small></div>
        <button type="button" onClick={onClose} aria-label="关闭素材库"><X size={18} /></button>
      </header>
      <div className="canvas-library-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={mode === 'image'} className={mode === 'image' ? 'is-active' : ''} onClick={() => { setMode('image'); setSelected(null); }}><Images size={16} />图片<span>{imagesList.length}</span></button>
        <button type="button" role="tab" aria-selected={mode === 'video'} className={mode === 'video' ? 'is-active' : ''} onClick={() => { setMode('video'); setSelected(null); }}><Film size={16} />视频<span>{videosList.length}</span></button>
      </div>
      <div className="canvas-library-grid">
        {assets.map((asset) => <button type="button" draggable className={`canvas-library-card ${selected?.id === asset.id ? 'is-selected' : ''}`} key={asset.id} onDragStart={(event) => beginDrag(event, asset)} onClick={() => choose(asset)}>
          <span className="canvas-library-card__media" style={{ aspectRatio: assetRatio(asset) }}>
            {asset.kind === 'image'
              ? <img src={asset.previewUrl || asset.url} alt="" loading="lazy" decoding="async" />
              : asset.poster
                ? <img src={asset.poster} alt="" loading="lazy" decoding="async" />
                : <video src={asset.url} muted preload="metadata" playsInline />}
            <i>{asset.kind === 'image' ? <ImageIcon size={14} /> : <Film size={14} />}</i>
            {selected?.id === asset.id && <b><Check size={15} /></b>}
          </span>
          <strong>{suggestedName(asset)}</strong>
          <small>{assetRatio(asset).replace(' / ', ':')}{asset.duration ? ` · ${asset.duration}秒` : ''}</small>
        </button>)}
        {!assets.length && <div className="canvas-library-empty">{mode === 'image' ? <ImageIcon size={26} /> : <Film size={26} />}<strong>暂无历史{mode === 'image' ? '图片' : '视频'}</strong></div>}
      </div>
      <footer>
        <label><span>加入画布后的名称</span><input maxLength={100} value={name} onChange={(event) => setName(event.target.value)} disabled={!selected} placeholder={selected?.fallbackName || '先选择一项素材'} /></label>
        <button type="button" disabled={!selected} onClick={() => selected && onAdd?.(selected, name.trim() || selected.fallbackName)}><Plus size={16} />加入画布</button>
      </footer>
    </section>
  </div>;
}
